import React, { useState, useEffect } from 'react';
import { isSuperAdmin, loginSuperAdmin, logoutSuperAdmin } from './utils/auth';

export default function SuperAdmin() {
  const [authenticated, setAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [totalVisitors, setTotalVisitors] = useState(0);
  const [recentVisits, setRecentVisits] = useState([]);
  const [events, setEvents] = useState([]);
  const [categories, setCategories] = useState([]);
  const [houses, setHouses] = useState([]);
  const [resultsMap, setResultsMap] = useState({});

  // Form states
  const [newEvent, setNewEvent] = useState({ name: '', category_id: '' });
  const [editingResult, setEditingResult] = useState(null);
  const [editForm, setEditForm] = useState({
    student_name: '',
    class_name: '',
    house_id: ''
  });

  // Fetch data
  useEffect(() => {
    if (!isSuperAdmin()) return;
    setAuthenticated(true);
    fetchData();
  }, []);
  
  const fetchData = async () => {
    const [eventsRes, catsRes, housesRes] = await Promise.all([
      fetch('/api/events'),
      fetch('/api/categories'),
      fetch('/api/houses')
    ]);
    const evts = await eventsRes.json();
    setEvents(evts);
    setCategories(await catsRes.json());
    setHouses(await housesRes.json());
  
    // Fetch all results
    const resultMap = {};
    await Promise.all(
      evts.map(async (ev) => {
        const res = await fetch(`/api/results/${ev.id}`);
        resultMap[ev.id] = await res.json();
      })
    );
    setResultsMap(resultMap);
  };

  const fetchVisitors = async () => {
    const [totalRes, recentRes] = await Promise.all([
      fetch('/api/visitors/total'),
      fetch('/api/visitors/recent')
    ]);
    setTotalVisitors((await totalRes.json()).total);
    setRecentVisits(await recentRes.json());
  };

  useEffect(() => {
    if (authenticated) {
      fetchData();
      fetchVisitors();
    }
  }, [authenticated]);
  const handleLogin = (e) => {
    e.preventDefault();
    if (loginSuperAdmin(password)) {
      setAuthenticated(true);
      fetchData();
    } else {
      setError('Incorrect super admin password');
    }
  };

  const handleLogout = () => {
    logoutSuperAdmin();
    setAuthenticated(false);
  };

  // Add new event
  const handleAddEvent = async (e) => {
    e.preventDefault();
    await fetch('/api/events', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(newEvent)
    });
    setNewEvent({ name: '', category_id: '' });
    fetchData();
  };


  // Delete result
  const handleDeleteResult = async (eventId, resultId) => {
    if (!window.confirm('Delete this result?')) return;
    await fetch(`/api/results/${resultId}`, { method: 'DELETE' });
    fetchData();
  };

  // Start editing
  const startEdit = (eventId, result) => {
    setEditingResult({ eventId, result });
    setEditForm({
      student_name: result.student_name,
      class_name: result.class_name,
      house_id: result.house_id
    });
  };

  // Save edit
  const saveEdit = async () => {
    const { eventId, result } = editingResult;
    await fetch(`/api/results/${result.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(editForm)
    });
    setEditingResult(null);
    fetchData();
  };

  // Print report
  const printReport = (event) => {
    const results = resultsMap[event.id] || [];
    const printContent = `
      <html>
      <head><title>${event.name} - Athleon Report</title></head>
      <body style="font-family: Arial, sans-serif; padding: 20px;">
        <h2 style="text-align: center; color: #1e40af;">AL MAQAR ENGLISH SCHOOL | BADARIYA NAGAR</h2>
        <h3 style="text-align: center; color: #dc2626;">🏆 Athleon Sports Meet 2025–2026 🏆</h3>
        <h4 style="text-align: center; margin: 20px 0;">${event.name} (${event.category})</h4>
        <table border="1" style="width: 100%; border-collapse: collapse; margin-top: 20px;">
          <thead>
            <tr>
              <th>Position</th>
              <th>Student Name</th>
              <th>Class</th>
              <th>House</th>
            </tr>
          </thead>
          <tbody>
            ${results.map(r => `
              <tr>
                <td>${r.position === 1 ? '🥇 1st' : r.position === 2 ? '🥈 2nd' : r.position === 3 ? '🥉 3rd' : r.position + 'th'}</td>
                <td>${r.student_name}</td>
                <td>Class ${r.class_name}</td>
                <td>${r.house_name}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
        <p style="text-align: center; margin-top: 30px;">🎉 Congratulations to all participants! 🎉</p>
      </body>
      </html>
    `;
    const win = window.open('', '_blank');
    win.document.write(printContent);
    win.document.close();
    win.focus();
    win.print();
  };

  if (!authenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="bg-white p-8 rounded shadow-md w-full max-w-md">
          <h2 className="text-2xl font-bold mb-6 text-center">🔐 Super Admin Login</h2>
          <form onSubmit={handleLogin}>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Super admin password"
              className="w-full p-3 border border-gray-300 rounded mb-4"
              autoFocus
            />
            {error && <p className="text-red-500 text-center mb-4">{error}</p>}
            <button type="submit" className="w-full bg-red-600 text-white py-3 rounded font-semibold">
              Login
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 max-w-6xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">👑 Super Admin Panel</h1>
        <button onClick={handleLogout} className="text-sm text-gray-600 underline">Logout</button>
      </div>

      {/* Add New Event */}
      <div className="bg-white p-6 rounded shadow mb-8">
        <h2 className="text-xl font-semibold mb-4">➕ Add New Event</h2>
        <form onSubmit={handleAddEvent} className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <input
            type="text"
            placeholder="Event name"
            value={newEvent.name}
            onChange={(e) => setNewEvent({ ...newEvent, name: e.target.value })}
            className="p-2 border border-gray-300 rounded"
            required
          />
          <select
            value={newEvent.category_id}
            onChange={(e) => setNewEvent({ ...newEvent, category_id: e.target.value })}
            className="p-2 border border-gray-300 rounded"
            required
          >
            <option value="">Select Category</option>
            {categories.map(cat => (
              <option key={cat.id} value={cat.id}>{cat.name}</option>
            ))}
          </select>
          <button type="submit" className="bg-green-600 text-white py-2 rounded">Add Event</button>
        </form>
      </div>
      // Add to JSX (e.g., below "Add New Event")
      <div className="bg-white p-6 rounded shadow mb-8">
        <h2 className="text-xl font-semibold mb-4">👥 Visitor Analytics</h2>
        <p className="text-2xl font-bold text-blue-600 mb-4">Total Unique Visitors: {totalVisitors}</p>
        
        <h3 className="font-medium mb-2">Recent Visits</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full border">
            <thead>
              <tr className="bg-gray-100">
                <th className="border p-2">IP</th>
                <th className="border p-2">Location</th>
                <th className="border p-2">Page</th>
                <th className="border p-2">Time</th>
              </tr>
            </thead>
            <tbody>
              {recentVisits.map((v, i) => (
                <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                  <td className="border p-2 text-sm font-mono">{v.ip}</td>
                  <td className="border p-2 text-sm">
                    {v.city}, {v.region}, {v.country}
                  </td>
                  <td className="border p-2 text-sm">{v.page}</td>
                  <td className="border p-2 text-sm">
                    {new Date(v.timestamp).toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {/* Event List with Actions */}
      <div className="space-y-6">
        {events.map(ev => (
          <div key={ev.id} className="bg-white p-6 rounded shadow">
            <div className="flex justify-between items-start">
              <div>
                <h3 className="text-lg font-bold">{ev.name}</h3>
                <p className="text-gray-600">{ev.category}</p>
              </div>
              <div className="space-x-2">
                <button
                  onClick={() => printReport(ev)}
                  className="text-blue-600 hover:text-blue-800 text-sm"
                >
                  🖨️ Print Report
                </button>
              </div>
            </div>

            {/* Results Table */}
            {resultsMap[ev.id] && resultsMap[ev.id].length > 0 ? (
              <table className="w-full mt-4 border-collapse">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border p-2">Position</th>
                    <th className="border p-2">Student</th>
                    <th className="border p-2">Class</th>
                    <th className="border p-2">House</th>
                    <th className="border p-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {resultsMap[ev.id].map(r => (
                    <tr key={r.id || r.position}>
                      <td className="border p-2">{r.position === 1 ? '🥇 1st' : r.position === 2 ? '🥈 2nd' : r.position === 3 ? '🥉 3rd' : r.position + 'th'}</td>
                      <td className="border p-2">{r.student_name}</td>
                      <td className="border p-2">Class {r.class_name}</td>
                      <td className="border p-2">{r.house_name}</td>
                      <td className="border p-2">
                        <button
                          onClick={() => startEdit(ev.id, r)}
                          className="text-sm text-green-600 mr-2"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteResult(ev.id, r.id)}
                          className="text-sm text-red-600"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="mt-4 text-gray-500 italic">No results yet</p>
            )}
          </div>
        ))}
      </div>

      {/* Edit Modal */}
      {editingResult && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white p-6 rounded shadow-lg w-full max-w-md">
            <h3 className="text-lg font-bold mb-4">Edit Result</h3>
            <div className="space-y-3">
              <input
                type="text"
                placeholder="Student Name"
                value={editForm.student_name}
                onChange={(e) => setEditForm({ ...editForm, student_name: e.target.value })}
                className="w-full p-2 border border-gray-300 rounded"
              />
              <input
                type="text"
                placeholder="Class"
                value={editForm.class_name}
                onChange={(e) => setEditForm({ ...editForm, class_name: e.target.value })}
                className="w-full p-2 border border-gray-300 rounded"
              />
              <select
                value={editForm.house_id}
                onChange={(e) => setEditForm({ ...editForm, house_id: e.target.value })}
                className="w-full p-2 border border-gray-300 rounded"
              >
                <option value="">Select House</option>
                {houses.map(h => (
                  <option key={h.id} value={h.id}>{h.name}</option>
                ))}
              </select>
            </div>
            <div className="mt-4 flex justify-end space-x-2">
              <button
                onClick={() => setEditingResult(null)}
                className="px-4 py-2 text-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={saveEdit}
                className="px-4 py-2 bg-green-600 text-white rounded"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}